/*
 * BM-2 — Bushing Monitor. 1º módulo da parede de montagem (ver
 * docs/specs/cma-modulos-auxiliares-3d.md §4 e §5.1) e o que fixa a carcaça
 * DIN que SDB e SDV-V2 reaproveitam (ver geometriaBM2.js).
 */
/* eslint-disable no-param-reassign */
import * as THREE from 'three';
import { buildCorpoDin } from './geometriaBM2';
import { criarDecalCanvas } from './canvasDecal';
import { box, cylAxis } from '../geometriaHelpers';

// LEDs/rótulos do painel frontal, em fração do painel (0..1, origem no
// canto inferior-esquerdo) — a mesma lista alimenta o texto do decalque
// (desenharFrentePainel) e os discos 3D emissivos (adicionarLeds), então os
// dois ficam sempre alinhados. Layout lido em BM2 (1).png/(2).png (ver §5.1
// da spec).
const LEDS_FRENTE = [
  {
    frac: [0.115, 0.895], cor: 'ledVerdeAceso', rotulo: 'Status', alinhamento: 'direita',
  },
  {
    frac: [0.115, 0.765], cor: 'botao', rotulo: 'Default Address', alinhamento: 'direita', raio: 0.0055,
  },
  {
    frac: [0.145, 0.635], cor: 'ledVermelhoApagado', rotulo: 'I. Leakage', alinhamento: 'direita', destaque: true,
  },
  {
    frac: [0.145, 0.545], cor: 'ledVermelhoApagado', rotulo: 'Capacit.', alinhamento: 'direita',
  },
  {
    frac: [0.145, 0.455], cor: 'ledVermelhoApagado', rotulo: 'Tg. Delta', alinhamento: 'direita',
  },
  {
    frac: [0.115, 0.335], cor: 'ledAmbarApagado', rotulo: 'Self diag.', alinhamento: 'direita',
  },
];

function desenharFrentePainel(ctx, w, h) {
  const fracToPx = ([fx, fy]) => [fx * w, (1 - fy) * h];

  // marca d'água "BM", diagonal, mais escura que o painel
  ctx.save();
  ctx.translate(w * 0.66, h * 0.5);
  ctx.rotate(-0.06);
  ctx.font = `bold ${Math.round(h * 0.62)}px Arial`;
  ctx.fillStyle = 'rgba(30,32,34,0.55)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('BM', 0, 0);
  ctx.restore();

  // logo Treetech (glifo de 6 pontos + texto), canto superior direito
  const logoX = w * 0.86;
  const logoY = h * 0.90;
  const dotR = w * 0.009;
  const dotOffsets = [
    [0, -2.2], [-1.4, -1.1], [1.4, -1.1], [-1.4, 1.1], [1.4, 1.1], [0, 2.6],
  ];
  ctx.fillStyle = '#49b36e';
  dotOffsets.forEach(([dx, dy]) => {
    ctx.beginPath();
    ctx.arc(logoX + dx * dotR, logoY + dy * dotR, dotR, 0, Math.PI * 2);
    ctx.fill();
  });

  // rótulos dos LEDs/botão
  ctx.font = `600 ${Math.round(h * 0.042)}px Arial`;
  ctx.textBaseline = 'middle';
  LEDS_FRENTE.forEach(({
    frac, rotulo, alinhamento, destaque,
  }) => {
    const [px, py] = fracToPx(frac);
    const dx = alinhamento === 'direita' ? w * 0.028 : -w * 0.028;
    ctx.textAlign = alinhamento === 'direita' ? 'left' : 'right';
    if (destaque) {
      const metrics = ctx.measureText(rotulo);
      const padX = w * 0.012;
      ctx.fillStyle = 'rgba(73,179,110,0.9)';
      ctx.fillRect(px + dx - padX, py - h * 0.028, metrics.width + padX * 2, h * 0.056);
      ctx.fillStyle = '#0d1f13';
    } else {
      ctx.fillStyle = '#e9ebec';
    }
    ctx.fillText(rotulo, px + dx, py);
  });

  // colchete verde do grupo "Alarms", ao lado dos 3 LEDs de alarme
  ctx.strokeStyle = '#49b36e';
  ctx.lineWidth = Math.max(1, h * 0.006);
  const bx = w * 0.075;
  const [, yTop] = fracToPx([0, 0.66]);
  const [, yBottom] = fracToPx([0, 0.43]);
  ctx.beginPath();
  ctx.moveTo(bx + w * 0.015, yTop);
  ctx.lineTo(bx, yTop);
  ctx.lineTo(bx, yBottom);
  ctx.lineTo(bx + w * 0.015, yBottom);
  ctx.stroke();
  ctx.save();
  ctx.translate(bx - w * 0.018, (yTop + yBottom) / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillStyle = '#49b36e';
  ctx.font = `600 ${Math.round(h * 0.032)}px Arial`;
  ctx.textAlign = 'center';
  ctx.fillText('Alarms', 0, 0);
  ctx.restore();

  // conector LAN/SETUP e texto de rodapé
  ctx.fillStyle = '#e9ebec';
  ctx.font = `600 ${Math.round(h * 0.034)}px Arial`;
  ctx.textAlign = 'left';
  const [lanX, lanY] = fracToPx([0.10, 0.10]);
  ctx.strokeStyle = '#cfd2d4';
  ctx.lineWidth = Math.max(1, h * 0.004);
  ctx.strokeRect(lanX - w * 0.02, lanY - h * 0.03, w * 0.09, h * 0.06);
  ctx.fillText('LAN', lanX - w * 0.02, lanY - h * 0.05);
  ctx.fillText('SETUP', lanX - w * 0.02, lanY + h * 0.06);

  ctx.textAlign = 'right';
  ctx.font = `600 ${Math.round(h * 0.036)}px Arial`;
  ctx.fillText('Bushing Monitor', w * 0.94, h * 0.955);
}

function adicionarLeds(group, materiais, panelWidth, panelHeight, frenteZ) {
  LEDS_FRENTE.forEach(({ frac, cor, raio }) => {
    const [fx, fy] = frac;
    const x = (fx - 0.5) * panelWidth;
    const y = (fy - 0.5) * panelHeight;
    const led = cylAxis(raio ?? 0.0045, 0.003, materiais[cor], x, y, frenteZ + 0.002, 'z', 16);
    group.add(led);
  });
}

/**
 * Bloco de terminais pluggáveis (verde, com pinos em cima) — reaproveitado
 * pelos 3 blocos do topo e o da base (ver §5.1, passo 5). `pinos` é só a
 * contagem; o desenho não precisa ser fiel pino a pino.
 */
function buildBlocoTerminais(materiais, pinos, largura, profundidade) {
  const altura = 0.022;
  const grupo = new THREE.Group();
  grupo.add(box(largura, altura, profundidade, materiais.terminal, 0, 0, 0));

  const pinoRaio = 0.0025;
  const pinoAltura = altura * 0.85;
  const passo = largura / (pinos + 1);
  for (let i = 0; i < pinos; i += 1) {
    const x = -largura / 2 + passo * (i + 1);
    const pino = new THREE.Mesh(
      new THREE.CylinderGeometry(pinoRaio, pinoRaio, pinoAltura, 8),
      materiais.pino,
    );
    pino.position.set(x, altura / 2, 0);
    grupo.add(pino);
  }
  return grupo;
}

function adicionarBlocosDeTerminais(group, materiais, corpo) {
  const blocoZ = -corpo.depth * 0.15;

  // topo: 2 blocos maiores + 1 menor, como em BM2 (1)/(3).png
  const topBlocos = [
    { pinos: 7, largura: 0.052 },
    { pinos: 6, largura: 0.046 },
    { pinos: 4, largura: 0.032 },
  ];
  let cursorX = -corpo.width / 2 + 0.01;
  topBlocos.forEach(({ pinos, largura }) => {
    const bloco = buildBlocoTerminais(materiais, pinos, largura, corpo.depth * 0.45);
    bloco.position.set(cursorX + largura / 2, corpo.topY, blocoZ);
    group.add(bloco);
    cursorX += largura + 0.01;
  });

  // base: 1 bloco, centralizado
  const blocoBase = buildBlocoTerminais(materiais, 7, 0.052, corpo.depth * 0.45);
  blocoBase.rotation.z = Math.PI;
  blocoBase.position.set(0, corpo.bottomY, blocoZ);
  group.add(blocoBase);
}

export function buildBm2(materiais) {
  const corpo = buildCorpoDin(materiais);
  const { group } = corpo;
  group.name = 'bm2';

  const texturaFrente = criarDecalCanvas(desenharFrentePainel, 1024, 768);
  if (texturaFrente) {
    const materialDecal = new THREE.MeshStandardMaterial({
      map: texturaFrente, transparent: true, roughness: 0.6, metalness: 0,
    });
    const decal = box(
      corpo.panelWidth * 0.99, corpo.panelHeight * 0.99, 0.0015,
      materialDecal, 0, 0, corpo.panelFrontZ + 0.001,
    );
    group.add(decal);
  }

  adicionarLeds(group, materiais, corpo.panelWidth, corpo.panelHeight, corpo.panelFrontZ);
  adicionarBlocosDeTerminais(group, materiais, corpo);

  return {
    id: 'bm2',
    group,
    width: corpo.frameWidth,
    height: corpo.height + 0.022 * 2, // corpo + os dois blocos de terminais (topo/base)
    depth: corpo.depth,
  };
}

export default buildBm2;
